import Field from "../interfaces/Field";
import FieldConfig from "../interfaces/FieldConfig";
import FieldConfigMap from "../interfaces/FieldConfigMap";
import FieldGroup from "../interfaces/FieldGroup";
import FieldSelectionMap from "../interfaces/FieldSelectionMap";

export const createFieldMap = (
  fieldGroups: FieldGroup[],
  selectedFieldIds?: string[]
) => {
  const map: FieldSelectionMap = {};
  const fields = fieldGroups.flatMap((group) => group.fields);

  if (selectedFieldIds) {
    fields.forEach(
      (field) => (map[field.fieldId] = selectedFieldIds.includes(field.fieldId))
    );
  } else {
    fields.forEach((field) => (map[field.fieldId] = false));
  }

  return map;
};

export const createFieldConfigMap = (
  allFields: Field[],
  fieldConfigs: FieldConfig[]
) => {
  return fieldConfigs.reduce((acc, val, index) => {
    const field = allFields.find((f) => f.fieldId === val.fieldId);

    if (field) {
      acc[field.dataSourceName] = { orderIndex: index, width: val.width };
    }
    return acc;
  }, {} as FieldConfigMap);
};

export const createConfigsFromFieldMaps = (
  defaultFieldGroups: FieldGroup[],
  nonDefaultFieldGroups: FieldGroup[],
  fieldSelectionMap: FieldSelectionMap,
  fieldConfigMap: FieldConfigMap
) => {
  const selectedDefaultFields = getUniqueFieldsFromGroups(defaultFieldGroups);
  const selectedNonDefaultFields = getUniqueAndSelectedFieldsFromGroups(
    nonDefaultFieldGroups,
    fieldSelectionMap
  );

  const selectedFields = [
    ...selectedDefaultFields,
    ...selectedNonDefaultFields,
  ];

  const fieldConfigs = selectedFields.map((field) => ({
    fieldId: field.fieldId,
    orderIndex: fieldConfigMap[field.dataSourceName]?.orderIndex,
    width: fieldConfigMap[field.dataSourceName]?.width ?? null,
  }));

  fieldConfigs.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  return fieldConfigs.map(({ fieldId, width }) => ({ fieldId, width }));
};

export const getUniqueFieldsFromGroups = (fieldGroups: FieldGroup[]) => {
  return fieldGroups
    .flatMap((group) => group.fields)
    .filter(
      (field, index, self) =>
        index === self.findIndex((f) => f.fieldId === field.fieldId)
    );
};

export const getUniqueFieldsFromGroupsBySearchQuery = (
  fieldGroups: FieldGroup[],
  query: string
) => {
  return fieldGroups
    .flatMap((group) => group.fields)
    .filter(
      (field, index, self) =>
        field.displayName.toLowerCase().includes(query.toLowerCase()) &&
        index === self.findIndex((f) => f.fieldId === field.fieldId)
    );
};

export const getUniqueAndSelectedFieldsFromGroups = (
  fieldGroups: FieldGroup[],
  selectionFieldMap: FieldSelectionMap
) => {
  return fieldGroups
    .flatMap((group) => group.fields)
    .filter(
      (field, index, self) =>
        selectionFieldMap[field.fieldId] &&
        index === self.findIndex((f) => f.fieldId === field.fieldId)
    );
};
